import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
	POSConnectorClient,
	type Payload,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";
import { never } from "../../../shared/never";
import * as transport from "../../../transport";
import { createDone } from "../shared/create-done";
import { DeclineResponse_fromPurchasePosConnectorResponse } from "../shared/declined-response";
import { getPageProps } from "../shared/get-declined-page-props";
import {
	AcknowledgedResponse,
	CancelRequest,
	createCloseHandlers,
	createMessageGuard,
	createReconnector,
	createRequestIdGuard,
	STATE_INITIAL_TIMEOUT,
	STATE_TRANSACTION_STATE_TIMEOUT,
} from "../shared/pos-connector-session";
import type { Route } from "../shared/route";
import { useApiClient } from "../shared/use-api-client";
import { useHealthcheck } from "../shared/use-healthcheck";
import { getApprovedResponse } from "./purchase-with-terminal/get-approved-response";
import { Page } from "./purchase-with-terminal/page";
import { render } from "./purchase-with-terminal/render";
import {
	PurchaseTransactionEventSchema,
	PurchaseAcknowledgedSchema,
	PurchaseResponseSchema,
	PurchaseTerminalBusySchema,
	PurchaseSignatureVerificationSchema,
	type PurchaseResponse,
} from "./schemas";

const resolve = transport.Resolve<"Terminal_purchase">();

export namespace PurchaseWithTerminal {
	export type Props = {
		request: Route.Request<"Terminal_purchase">;
	};
}

export function PurchaseWithTerminal(props: PurchaseWithTerminal.Props) {
	const apiClient = useApiClient();
	const healthcheck = useHealthcheck();

	const { request } = props;
	const [sessionId] = useState(() => request.query.sessionUuid ?? uuidv4());
	// Correlates every frame in one attempt; a retry mints a fresh id so stale
	// frames from the previous attempt are ignored.
	const requestIdRef = useRef(uuidv4());

	const [page, _setPage] = useState<Page>(() => Page.Initialising());
	const setPage = useCallback(
		(nextOrFn: Page | ((prev: Page) => Page)) => {
			if (!request.interactive) {
				return undefined;
			}
			return void _setPage((prev) => {
				const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
				logger.debug("purchase: navigation", {
					from: prev.$type,
					to: next.$type,
				});
				return next;
			});
		},
		[request.interactive],
	);

	useEffect(() => {
		const matchesRequestId = createRequestIdGuard(() => requestIdRef.current);

		transport.onCancel(() => {
			logger.info("purchase: external cancel called");

			return void done(
				Page.Cancelled({ onClose: closeWithCancelled }),
				"onClose",
				{ skip: true },
			);
		});

		let _timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		let _connection: POSConnectorClient | undefined = undefined;

		function dispose() {
			transport.onCancel(undefined);
			if (_timeout) _timeout = void clearTimeout(_timeout);
			if (_connection) _connection = void _connection.close();
		}

		function updateTimeout(callback: () => void, delay: number) {
			clearTimeout(_timeout);
			_timeout = setTimeout(callback, delay);
		}

		const done = createDone<Page>({
			dispose,
			setPage,
			interactive: request.interactive,
		});

		const _deviceId = request.headers.deviceId;
		if (!_deviceId) {
			return void (() => {
				logger.error("purchase: empty deviceId request header");

				return void done(
					Page.ErrorOperation({
						onClose: () =>
							void resolve.Error("Setup Required", {
								message: "Not configured.",
							}),
					}),
					"onClose",
				);
			})();
		}

		const deviceId = _deviceId;

		main();
		async function main(isRetry = false) {
			const onRetry = () => void main(true);

			if (isRetry) {
				logger.warn("purchase: retrying");
				requestIdRef.current = uuidv4();
			}

			done.isLocked = false;
			setPage(Page.Initialising());

			// Prevent continuation if token resolve _after_ timeout.
			if (done.isLocked) {
				return;
			}

			let _hasRequested = false;

			const reconnector = createReconnector({
				deviceId,
				healthcheck,
				updateTimeout,
				connect: () => void connect(),
				onExhausted: () =>
					void done(
						Page.ErrorDisconnected({
							onClose: closeWithConnectionFailure,
							onRetry,
						}),
						"onClose",
					),
				onNetworkError: () =>
					void done(
						Page.ErrorNetwork({
							onClose: closeWithNetworkFailure,
							onRetry,
						}),
						"onClose",
					),
			});

			const handle = createMessageGuard({
				onUnreachable: () =>
					void done(
						Page.ErrorTerminalUnreachable({
							onClose: closeWithConnectionFailure,
							onRetry,
						}),
						"onClose",
					),
				onForbidden: () =>
					void done(
						Page.ErrorTerminalMissing({
							onClose: closeWithConnectionFailure,
						}),
						"onClose",
					),
				onOperationError: () =>
					void done(
						Page.ErrorOperation({
							onClose: closeWithConnectionFailure,
							onRetry,
						}),
						"onClose",
					),
			});

			connect();
			async function connect() {
				logger.debug("purchase: connect", {
					attempt: reconnector.attempt,
				});

				updateTimeout(() => {
					return void done(
						Page.ErrorOperation({
							onClose: closeWithNetworkFailure,
							onRetry,
						}),
						"onClose",
					);
				}, STATE_INITIAL_TIMEOUT);

				const authToken = (await request.tokens())?.authAccessToken;

				// Prevent continuation if token resolve _after_ timeout.
				if (done.isLocked) {
					return;
				}

				if (!authToken) {
					return void (() => {
						logger.warn("purchase: not authenticated");
						return void done(
							Page.ErrorOperation({
								onClose: () =>
									void resolve.Error("Setup Required", {
										message: "Not authenticated.",
									}),
							}),
							"onClose",
						);
					})();
				}

				transport.onCancel(() => {
					logger.info("purchase: external cancel called");

					if (!connection.ready) {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled }),
							"onClose",
							{ skip: true },
						);
					}

					return void cancel(true);
				});

				const connection = POSConnectorClient({
					authToken,
					deviceUuid: deviceId,
					onOpen,
					onClose: reconnector.onClose,
					onError: reconnector.onError,
				});
				_connection = connection;

				function onOpen() {
					logger.debug("purchase: on open");

					if (!_hasRequested) {
						begin();
					} else {
						logger.warn("purchase: on open", {
							attempt: reconnector.attempt,
						});

						listen();
					}
				}

				function begin() {
					const payload = PurchaseRequest();

					logger.debug("purchase: request", {
						eventId: payload.eventId,
						transactionId: payload.data.transactionUuid,
					});

					updateTimeout(() => {
						connection.send(CancelRequest(sessionId));
						return void done(
							Page.ErrorInactivityTimeout({
								onClose: closeWithInactivityTimeout,
								onRetry,
							}),
							"onClose",
						);
					}, STATE_INITIAL_TIMEOUT);

					connection.onMessage(
						handle((message) => {
							{
								// This message is deprecated in favour of
								// `PurchaseResponseSchema`'s `data.responseCode` of
								// "Q9".
								const event =
									PurchaseTerminalBusySchema.safeParse(message).data;
								if (event) {
									return void (() => {
										return void done(
											Page.ErrorTerminalBusy({
												onClose: closeWithPairedDeviceBusy,
												onRetry,
											}),
											"onClose",
										);
									})();
								}
							}
							{
								const event =
									PurchaseAcknowledgedSchema.safeParse(message).data;
								if (event) {
									if (
										!matchesRequestId(event.requestId, {
											sessionUuid: event.data.sessionUuid,
										})
									) {
										return;
									}
									setPage(Page.InitialisingPayment());
									return void listen();
								}
							}
						}),
					);

					connection.send(payload);
					_hasRequested = true;
				}

				function listen() {
					inactivity();
					function inactivity() {
						updateTimeout(() => {
							connection.send(CancelRequest(sessionId));
							// NOTE: While the terminal might trigger a timeout error due to inactivity,
							// this fallback ensures we still display a status to the merchant if the
							// connection is completely lost.
							return void done(
								Page.ErrorInactivityTimeout({
									onClose: closeWithInactivityTimeout,
									onRetry,
								}),
								"onClose",
							);
						}, STATE_TRANSACTION_STATE_TIMEOUT);
					}

					function next(page: Page) {
						setPage(page);
						// Reset reconnection attempts when we successfully receive data.
						reconnector.reset();
						// Reset inactivity timeout on every navigation.
						inactivity();
					}

					connection.onMessage(
						handle((message) => {
							{
								const event = PurchaseResponseSchema.safeParse(message).data;
								if (event) {
									return void (() => {
										if (
											!matchesRequestId(event.requestId, {
												status: event.data.status,
											})
										) {
											return;
										}

										connection.send(
											AcknowledgedResponse({
												action: "purchase",
												sessionId,
												requestId: requestIdRef.current,
											}),
										);

										logger.debug("purchase: response", {
											status: event.data.status,
										});

										if (event.data.status === "APPROVED") {
											return void approved(event);
										} else {
											return void declined(event);
										}
									})();
								}
							}
							{
								const event =
									PurchaseTransactionEventSchema.safeParse(message).data;
								if (event) {
									return void (() => {
										logger.debug("purchase: transaction event", {
											event: event.data.event,
										});

										resolve.Event(event.data.event, {
											message: event.data.displayMessage,
										});

										const sendTransactionEvent = (
											event: "CANCEL" | "PROCEED" | "SKIP",
										) => {
											connection.send({
												action: "transactionEvent",
												eventId: uuidv4(),
												timestamp: Date.now().toString(),
												data: {
													event,
													sessionUuid: sessionId,
												},
											});
										};

										switch (event.data.event) {
											case "AWAITING_ACCOUNT_SELECTION":
												return next(
													Page.AwaitAccountSelection({
														onClose: cancel,
													}),
												);
											case "AWAITING_APPLICATION_SELECTION":
												return next(
													Page.AwaitApplicationSelection({
														onClose: cancel,
													}),
												);
											case "AWAITING_PAYMENT_METHOD":
												return next(
													Page.AwaitPaymentMethod({
														onClose: cancel,
													}),
												);
											case "AWAITING_PIN_ENTRY":
												return next(
													Page.AwaitPinEntry({
														onClose: cancel,
													}),
												);
											case "AWAITING_TIP_ENTRY":
												return next(
													Page.AwaitTipEntry({
														onClose: cancel,
														onSkip: () => sendTransactionEvent("SKIP"),
													}),
												);
											case "PROCESSING":
												return next(Page.AwaitProcessing());
											case "DISPLAYING_PAYMENT_MESSAGE":
											case "RETRYING_PAYMENT_METHOD":
												return next(
													Page.DisplayPaymentMessage({
														onClose: cancel,
													}),
												);
											case "TIP_PROVIDED":
												return next(
													Page.TipProvided({
														message: event.data.displayMessage,
														onClose: cancel,
														/* v8 ignore next -- render maps TipProvided to an intermediate template with no Next button to trigger this */
														onNext: () => sendTransactionEvent("PROCEED"),
													}),
												);
											/* v8 ignore start -- exhaustiveness guard, unreachable for valid transaction event variants */
											default:
												logger.warn(
													"purchase: transaction event unhandled variant",
													{
														data: event.data,
													},
												);
												return never(event.data.event);
											/* v8 ignore stop */
										}
									})();
								}
							}
							{
								const event =
									PurchaseSignatureVerificationSchema.safeParse(message)?.data;
								if (event) {
									return void (() => {
										if (!event.requestId) {
											logger.warn("purchase: signature missing requestId", {
												state: event.data.state,
											});
										}

										logger.warn("purchase: signature verification", {
											state: event.data.state,
										});

										resolve.Event(event.data.state, {
											message: event.data.signature,
										});

										switch (event.data.state) {
											case "AWAITING_SIGNATURE":
											case "SIGNATURE_CLEARED":
												return next(
													Page.AwaitSignature({
														onClose: cancel,
													}),
												);
											case "SIGNATURE_SUBMITTED": {
												const sendSignatureVerificationEvent = (
													isValid: boolean,
												) => {
													connection.send({
														action: "signatureVerification",
														eventId: uuidv4(),
														requestId: event.requestId,
														timestamp: Date.now().toString(),
														data: {
															event: "SIGNATURE_VERIFICATION",
															isValid,
															sessionUuid: sessionId,
														},
													});
												};
												return next(
													Page.SignatureSubmitted({
														signature: event.data.signature,
														onClose: closeWithCancelled,
														onApprove: () =>
															sendSignatureVerificationEvent(true),
														onDecline: () =>
															sendSignatureVerificationEvent(false),
													}),
												);
											}
											/* v8 ignore start -- exhaustiveness guard, unreachable for valid signature states */
											default:
												logger.warn(
													"purchase: signature verification event unhandled variant",
													{
														data: event.data,
													},
												);
												return never(event.data.state);
											/* v8 ignore stop */
										}
									})();
								}
							}
						}),
					);
				}

				function approved(event: PurchaseResponse) {
					const { response, getPageProps } = getApprovedResponse({
						event: event.data,
						reference: request.query.reference,
					});
					return void done(
						Page.Approved({
							...getPageProps(),
							onDone: () => void resolve(response),
						}),
						"onDone",
					);
				}

				function declined(event: PurchaseResponse) {
					const response = DeclineResponse_fromPurchasePosConnectorResponse({
						data: event.data,
						reference: request.query.reference,
					});
					return void done(
						Page.Declined({
							...getPageProps({ data: event.data }),
							onCancel: () => void resolve(response),
							onRetry,
						}),
						"onCancel",
					);
				}

				function cancel(external = false) {
					/* v8 ignore start -- UI close handlers forward the click event, so `external` is always truthy here; the falsy branch is only reachable via a direct zero-arg call */
					if (!external) {
						logger.info("purchase: internal cancel called");
					}
					/* v8 ignore stop */

					setPage((prev) => {
						return { ...prev, cancelling: true };
					});

					connection.onMessage(
						handle((message) => {
							const event = PurchaseResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									if (
										!matchesRequestId(event.requestId, {
											status: event.data.status,
										})
									) {
										return;
									}

									connection.send(
										AcknowledgedResponse({
											action: "purchase",
											sessionId,
											requestId: requestIdRef.current,
										}),
									);

									logger.debug("purchase: response", {
										status: event.data.status,
									});

									if (event.data.status === "APPROVED") {
										return approved(event);
									} else {
										const response =
											DeclineResponse_fromPurchasePosConnectorResponse({
												data: event.data,
												reference: request.query.reference,
											});
										return void done(
											Page.Cancelled({
												onClose: () => void resolve(response),
											}),
											"onClose",
											{ skip: true },
										);
									}
								})();
							}
						}),
					);

					connection.send(CancelRequest(sessionId));
					updateTimeout(() => {
						return void done(
							Page.Cancelled({
								onClose: closeWithCancelled,
							}),
							"onClose",
						);
					}, STATE_TRANSACTION_STATE_TIMEOUT);
				}
			}
		}

		function PurchaseRequest() {
			const now = new Date();
			return {
				action: "purchase",
				eventId: uuidv4(),
				requestId: requestIdRef.current,
				timestamp: now.getTime().toString(),
				data: {
					// Currently required by backend.
					transactionUuid: uuidv4(),
					sessionUuid: sessionId,
					amount: request.query.amount,
					tipAmount: request.query.tipAmount ?? 0,
					externalReference: request.query.reference,
					timestamp: now.toISOString(),
					terminalContact: request.query.terminalContact,
					terminalNote: request.query.terminalNote,
					terminalReceipt: request.query.terminalReceipt,
					terminalReceiptPrint: request.query.terminalReceiptPrint,
				},
			} satisfies Payload;
		}

		return () => void dispose();
	}, [apiClient, healthcheck, request, sessionId, setPage]);

	return render(page);
}

const {
	closeWithCancelled,
	closeWithConnectionFailure,
	closeWithInactivityTimeout,
	closeWithNetworkFailure,
	closeWithPairedDeviceBusy,
} = createCloseHandlers({ resolveError: resolve.Error });
